'use client';

import DOMPurify from 'dompurify';

export type HtmlLinkEntry = {
  type: 'anchor' | 'image' | 'media';
  text: string;
  url: string;
  rel: string | null;
  target: string | null;
};

export type HtmlValidationIssue = {
  severity: 'error' | 'warning' | 'info';
  title: string;
  description: string;
};

export type MetaPreviewData = {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  siteName: string;
  url: string;
  image: string;
  twitterCard: string;
};

export type SchemaSummary = {
  rootType: string | null;
  entityCount: number;
  entityTypes: string[];
  primaryName: string | null;
  primaryUrl: string | null;
};

function sanitizeHtml(html: string) {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });
}

// The html profile has no iframe, which is the one tag an embed is made of.
// Allow it back with an attribute allow-list; srcdoc and the on* handlers stay out.
function sanitizeEmbedHtml(html: string) {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'height',
      'loading',
      'referrerpolicy',
      'sandbox',
      'scrolling',
      'src',
      'title',
      'width',
    ],
    FORBID_ATTR: ['srcdoc'],
  });
}

// Void elements have no closing tag. Serializing <br></br> makes the parser
// read the close tag as a second <br>, which doubles every line break.
const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

function normalizeLineEndings(value: string) {
  return value.replace(/\r\n?/g, '\n');
}

function parseHtmlDocument(input: string) {
  const parser = new DOMParser();
  const normalized =
    input.trim().startsWith('<html') || input.includes('<body') || input.includes('<head')
      ? input
      : `<!doctype html><html><head></head><body>${input}</body></html>`;
  const document = parser.parseFromString(normalized, 'text/html');
  return document;
}

function escapeAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function serializeAttributes(element: Element) {
  return Array.from(element.attributes)
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((attribute) => ` ${attribute.name}="${escapeAttribute(attribute.value)}"`)
    .join('');
}

function shouldPreserveTextWhitespace(tagName: string) {
  return ['pre', 'code', 'script', 'style', 'textarea'].includes(tagName);
}

function formatNode(node: Node, level: number): string[] {
  const indent = '  '.repeat(level);

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    const normalized = text.replace(/\s+/g, ' ').trim();
    return normalized ? [`${indent}${normalized}`] : [];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();
  const attrs = serializeAttributes(element);
  const children = Array.from(element.childNodes);

  if (VOID_ELEMENTS.has(tagName)) {
    return [`${indent}<${tagName}${attrs}>`];
  }

  if (!children.length) {
    return [`${indent}<${tagName}${attrs}></${tagName}>`];
  }

  if (shouldPreserveTextWhitespace(tagName)) {
    return [`${indent}<${tagName}${attrs}>${element.innerHTML}</${tagName}>`];
  }

  const childLines = children.flatMap((child) => formatNode(child, level + 1));
  if (!childLines.length) {
    return [`${indent}<${tagName}${attrs}></${tagName}>`];
  }

  return [`${indent}<${tagName}${attrs}>`, ...childLines, `${indent}</${tagName}>`];
}

function getBodyInnerHtml(document: Document) {
  return document.body?.innerHTML?.trim() ?? '';
}

function collapseWhitespaceText(text: string) {
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractTextFromNode(node: Node, rich = false): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (['script', 'style', 'noscript'].includes(tag)) {
    return '';
  }

  const childText = Array.from(element.childNodes)
    .map((child) => extractTextFromNode(child, rich))
    .join('');

  if (tag === 'br') {
    return '\n';
  }

  if (rich && ['li'].includes(tag)) {
    return `- ${childText.trim()}\n`;
  }

  if (
    rich &&
    ['p', 'div', 'section', 'article', 'header', 'footer', 'aside', 'blockquote'].includes(tag)
  ) {
    return `${childText.trim()}\n\n`;
  }

  if (rich && /^h[1-6]$/.test(tag)) {
    return `${childText.trim()}\n\n`;
  }

  return childText;
}

function getMetaContent(document: Document, key: string, attribute: 'name' | 'property') {
  const element = document.head?.querySelector(`meta[${attribute}="${key}"]`);
  return element?.getAttribute('content')?.trim() ?? '';
}

function getLinkHref(document: Document, rel: string) {
  return document.head?.querySelector(`link[rel="${rel}"]`)?.getAttribute('href')?.trim() ?? '';
}

function ensureHttpLikeUrl(url: string) {
  return /^https?:\/\//i.test(url) ? url : '';
}

function uniqueStrings(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)));
}

export function formatHtml(input: string) {
  const document = parseHtmlDocument(input);
  const hasHeadContent = document.head && document.head.childNodes.length > 0;
  const hasBodyContent = document.body && document.body.childNodes.length > 0;
  const lines: string[] = [];

  if (hasHeadContent) {
    lines.push('<head>');
    lines.push(...Array.from(document.head.childNodes).flatMap((node) => formatNode(node, 1)));
    lines.push('</head>');
  }

  if (hasBodyContent) {
    lines.push('<body>');
    lines.push(...Array.from(document.body.childNodes).flatMap((node) => formatNode(node, 1)));
    lines.push('</body>');
  }

  if (!lines.length) {
    throw new Error('Paste some HTML first.');
  }

  return normalizeLineEndings(lines.join('\n')).trim();
}

export function minifyHtml(input: string) {
  const document = parseHtmlDocument(input);
  document.querySelectorAll('*').forEach((element) => {
    if (!(element instanceof Element)) {
      return;
    }

    Array.from(element.childNodes).forEach((child) => {
      if (child.nodeType !== Node.COMMENT_NODE) {
        return;
      }
      child.parentNode?.removeChild(child);
    });

    if (!shouldPreserveTextWhitespace(element.tagName.toLowerCase())) {
      Array.from(element.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.textContent = child.textContent?.replace(/\s+/g, ' ') ?? '';
        }
      });
    }
  });

  // A pasted full page keeps its <head>. Only a bare fragment collapses to the body.
  const hasHeadContent = (document.head?.childNodes.length ?? 0) > 0;
  const output = hasHeadContent
    ? document.documentElement.outerHTML
    : getBodyInnerHtml(document) || document.documentElement.outerHTML;

  // Whitespace inside pre, textarea, script and style is significant. Park those
  // blocks before the string passes and put them back afterwards.
  const parked: string[] = [];
  const guarded = output.replace(
    /<(pre|textarea|script|style)\b[^>]*>[\s\S]*?<\/\1>/gi,
    (match) => {
      parked.push(match);
      return ` cleanorparked${parked.length - 1} `;
    },
  );

  return guarded
    .replace(/>\s+</g, '><')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/ cleanorparked(\d+) /g, (_, index) => parked[Number(index)]);
}

export function stripHtmlTags(input: string) {
  const document = parseHtmlDocument(input);
  return collapseWhitespaceText(
    extractTextFromNode(document.body || document.documentElement, false).replace(/\s+/g, ' '),
  );
}

export function htmlToText(input: string) {
  const document = parseHtmlDocument(input);
  return collapseWhitespaceText(
    extractTextFromNode(document.body || document.documentElement, true).replace(
      /\n[ \t]+\n/g,
      '\n\n',
    ),
  );
}

export function extractLinksFromHtml(input: string): HtmlLinkEntry[] {
  const document = parseHtmlDocument(input);
  const output: HtmlLinkEntry[] = [];

  document.querySelectorAll('a[href]').forEach((element) => {
    output.push({
      type: 'anchor',
      text: (element.textContent || '(link)').trim() || '(link)',
      url: element.getAttribute('href')?.trim() ?? '',
      rel: element.getAttribute('rel'),
      target: element.getAttribute('target'),
    });
  });

  document.querySelectorAll('img[src]').forEach((element) => {
    output.push({
      type: 'image',
      text: element.getAttribute('alt')?.trim() || '(image)',
      url: element.getAttribute('src')?.trim() ?? '',
      rel: null,
      target: null,
    });
  });

  document.querySelectorAll('video[src], audio[src], source[src]').forEach((element) => {
    output.push({
      type: 'media',
      text: element.tagName.toLowerCase(),
      url: element.getAttribute('src')?.trim() ?? '',
      rel: null,
      target: null,
    });
  });

  const seen = new Set<string>();
  return output.filter((entry) => {
    const key = `${entry.type}:${entry.text}:${entry.url}:${entry.rel ?? ''}:${entry.target ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function inspectMetaTags(input: string): MetaPreviewData {
  const document = parseHtmlDocument(input);
  const title = document.title.trim();
  const description = getMetaContent(document, 'description', 'name');
  const canonical = getLinkHref(document, 'canonical');
  const robots = getMetaContent(document, 'robots', 'name');
  const siteName = getMetaContent(document, 'og:site_name', 'property');
  const url = getMetaContent(document, 'og:url', 'property') || canonical;
  const image =
    getMetaContent(document, 'og:image', 'property') ||
    getMetaContent(document, 'twitter:image', 'name');
  const twitterCard = getMetaContent(document, 'twitter:card', 'name');

  return {
    title,
    description,
    canonical,
    robots,
    siteName,
    url,
    image,
    twitterCard,
  };
}

export function inspectOpenGraph(input: string) {
  const document = parseHtmlDocument(input);
  const meta = inspectMetaTags(input);

  return {
    title: getMetaContent(document, 'og:title', 'property') || meta.title,
    description: getMetaContent(document, 'og:description', 'property') || meta.description,
    url: getMetaContent(document, 'og:url', 'property') || meta.canonical || meta.url,
    image: getMetaContent(document, 'og:image', 'property') || meta.image,
    siteName: getMetaContent(document, 'og:site_name', 'property') || meta.siteName,
    type: getMetaContent(document, 'og:type', 'property') || 'website',
    twitterCard: getMetaContent(document, 'twitter:card', 'name') || meta.twitterCard,
    twitterTitle: getMetaContent(document, 'twitter:title', 'name'),
    twitterDescription: getMetaContent(document, 'twitter:description', 'name'),
    twitterImage: getMetaContent(document, 'twitter:image', 'name'),
  };
}

export function cleanEmbedCode(input: string) {
  const document = parseHtmlDocument(input);
  document.querySelectorAll('script').forEach((element) => element.remove());
  document.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }

      if (['style', 'srcdoc'].includes(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  const html = sanitizeEmbedHtml(getBodyInnerHtml(document) || document.documentElement.outerHTML);
  if (!html.trim()) {
    throw new Error('Nothing was left after cleaning. Check that the embed contains an iframe.');
  }

  // An embed is pasted into an existing page, so it must not carry a <body> wrapper.
  const formatted = formatHtml(html);
  const lines = normalizeLineEndings(formatted).split('\n');
  if (lines[0] === '<body>' && lines[lines.length - 1] === '</body>') {
    return lines
      .slice(1, -1)
      .map((line) => (line.startsWith('  ') ? line.slice(2) : line))
      .join('\n')
      .trim();
  }
  return formatted;
}

export function previewSchemaMarkup(input: string) {
  let value: unknown;
  try {
    value = JSON.parse(input);
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'The JSON-LD could not be parsed.');
  }

  const root = value as Record<string, unknown>;
  const entities = Array.isArray(root['@graph'])
    ? (root['@graph'] as Array<Record<string, unknown>>)
    : [root];
  const entityTypes = uniqueStrings(
    entities.flatMap((entity) => {
      const type = entity?.['@type'];
      if (Array.isArray(type)) {
        return type.map((item) => String(item));
      }
      return type ? [String(type)] : [];
    }),
  );

  const summary: SchemaSummary = {
    rootType: typeof root['@type'] === 'string' ? String(root['@type']) : (entityTypes[0] ?? null),
    entityCount: entities.length,
    entityTypes,
    primaryName:
      typeof root.name === 'string'
        ? root.name
        : typeof root.headline === 'string'
          ? root.headline
          : null,
    primaryUrl: typeof root.url === 'string' ? root.url : null,
  };

  return {
    prettyJson: JSON.stringify(value, null, 2),
    summary,
  };
}

export function validateHtml(input: string) {
  const document = parseHtmlDocument(input);
  const issues: HtmlValidationIssue[] = [];
  const meta = inspectMetaTags(input);
  const links = extractLinksFromHtml(input);

  const parserError = document.querySelector('parsererror');
  if (parserError) {
    issues.push({
      severity: 'error',
      title: 'Parser error detected',
      description: parserError.textContent?.trim() || 'The browser parser reported malformed HTML.',
    });
  }

  if (!meta.title) {
    issues.push({
      severity: 'warning',
      title: 'Missing title',
      description: 'The pasted markup does not include a <title> tag.',
    });
  }

  if (!meta.description) {
    issues.push({
      severity: 'warning',
      title: 'Missing meta description',
      description: 'The pasted markup does not include a meta description.',
    });
  }

  if (document.head.querySelectorAll('title').length > 1) {
    issues.push({
      severity: 'warning',
      title: 'Duplicate title tags',
      description: 'More than one <title> tag was found in the head.',
    });
  }

  if (document.head.querySelectorAll('meta[name="description"]').length > 1) {
    issues.push({
      severity: 'warning',
      title: 'Duplicate meta descriptions',
      description: 'More than one meta description tag was found.',
    });
  }

  if (meta.canonical && !ensureHttpLikeUrl(meta.canonical)) {
    issues.push({
      severity: 'warning',
      title: 'Canonical is not absolute',
      description:
        'The canonical link is present, but it does not look like an absolute http(s) URL.',
    });
  }

  const emptyAnchors = links.filter(
    (entry) => entry.type === 'anchor' && (!entry.url || entry.url === '#'),
  );
  if (emptyAnchors.length) {
    issues.push({
      severity: 'info',
      title: 'Empty or placeholder anchors',
      description: `${emptyAnchors.length} anchor link${emptyAnchors.length === 1 ? '' : 's'} use an empty or placeholder href.`,
    });
  }

  if (!document.querySelector('h1')) {
    issues.push({
      severity: 'info',
      title: 'No H1 found',
      description: 'The markup does not include an <h1>, which is often a useful content signal.',
    });
  }

  if (!document.body?.textContent?.trim()) {
    issues.push({
      severity: 'warning',
      title: 'Very little body content',
      description: 'The pasted markup has little or no readable body text.',
    });
  }

  if (!issues.length) {
    issues.push({
      severity: 'info',
      title: 'No obvious practical issues found',
      description:
        'This checker did not find common title, description, canonical, anchor, or structure problems in the pasted HTML.',
    });
  }

  return {
    issues,
    meta,
  };
}

export function createHtmlToolJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}
